import {
  Currency,
  type CurrencyConstructor,
} from '@webapp/core/entities/external/Currency'
import {
  Approver,
  type ApproverConstructor,
} from '@webapp/core/entities/internal/Approver'
import {
  PrimaryCategory,
  type PrimaryCategoryConstructor,
} from '@webapp/core/entities/internal/PrimaryCategory'
import {
  Requester,
  type RequesterConstructor,
} from '@webapp/core/entities/internal/Requester'
import {
  SecondaryCategory,
  type SecondaryCategoryConstructor,
} from '@webapp/core/entities/internal/SecondaryCategory'
import {
  type ExpenseStatusEnum,
  ExpenseStatuses,
} from '../enums/ExpenseStatusEnum'
import {
  ExpenseApprovalLog,
  type ExpenseApprovalLogConstructor,
} from '../ExpenseApprovalLog'
import { ExpenseItem, type ExpenseItemConstructor } from '../ExpenseItem'
import {
  ExpensePermissions,
  type ExpensePermissionsConstructor,
} from '../ExpensePermissions'
import { ExpenseTravel, type ExpenseTravelConstructor } from '../ExpenseTravel'
import {
  NextApproverChoice,
  type NextApproverChoiceConstructor,
} from '../NextApproverChoice'

export interface ExpenseConstructor {
  id: number
  uuid: string
  title: string
  status: ExpenseStatusEnum
  description: string
  submittedDate: ISODateString
  dateLastModified: ISODateString
  currency: CurrencyConstructor
  requester: RequesterConstructor
  primaryCategory: PrimaryCategoryConstructor
  secondaryCategory: SecondaryCategoryConstructor
  nextApprover: Nullable<ApproverConstructor>
  nextApproverChoices: NextApproverChoiceConstructor[]
  items: ExpenseItemConstructor[]
  logs: ExpenseApprovalLogConstructor[]
  totalAmount: number
  reimbursableAmount: Nullable<number>
  permissions: ExpensePermissionsConstructor
  travel: Nullable<ExpenseTravelConstructor>
  approvalDelegatee: Nullable<string>
  commentsCount: number
}

export class Expense {
  readonly id: number
  readonly uuid: string
  readonly title: string
  readonly status: ExpenseStatusEnum
  readonly description: string
  readonly submittedDate: Date
  readonly dateLastModified: Date
  readonly currency: Currency
  readonly requester: Requester
  readonly primaryCategory: PrimaryCategory
  readonly secondaryCategory: SecondaryCategory
  readonly nextApprover: Nullable<Approver>
  readonly nextApproverChoices: NextApproverChoice[]
  readonly items: ExpenseItem[]
  readonly logs: ExpenseApprovalLog[]
  readonly totalAmount: number
  readonly reimbursableAmount: number
  readonly permissions: ExpensePermissions
  readonly travel: Nullable<ExpenseTravel>
  readonly approvalDelegatee: Nullable<string>
  readonly commentsCount: number

  constructor(data: ExpenseConstructor) {
    this.id = data.id
    this.uuid = data.uuid
    this.title = data.title || ''
    this.status = data.status
    this.description = data.description || ''
    this.submittedDate = new Date(data.submittedDate)
    this.dateLastModified = new Date(data.dateLastModified)
    this.currency = new Currency(data.currency)
    this.requester = new Requester(data.requester)
    this.primaryCategory = new PrimaryCategory(data.primaryCategory)
    this.secondaryCategory = new SecondaryCategory(data.secondaryCategory)
    this.nextApprover = data.nextApprover
      ? new Approver(data.nextApprover)
      : null
    this.nextApproverChoices = (data.nextApproverChoices || []).map(
      (choice) => new NextApproverChoice(choice)
    )
    this.items = data.items.map((item) => new ExpenseItem(item))
    this.logs = data.logs.map((log) => new ExpenseApprovalLog(log))
    this.totalAmount = data.totalAmount || 0
    this.reimbursableAmount = data.reimbursableAmount || 0
    this.permissions = new ExpensePermissions(data.permissions)
    this.travel = data.travel ? new ExpenseTravel(data.travel) : null
    this.approvalDelegatee = data.approvalDelegatee || null
    this.commentsCount = data.commentsCount || 0
  }

  get isPending(): boolean {
    return this.status === ExpenseStatuses.PENDING
  }

  get isApproved(): boolean {
    return this.status === ExpenseStatuses.APPROVED
  }

  get isDenied(): boolean {
    return this.status === ExpenseStatuses.DENIED
  }

  get isResolved(): boolean {
    return this.isApproved || this.isDenied
  }

  get hasNextApproverChoices(): boolean {
    return this.nextApproverChoices.length > 0
  }

  get hasSingleApproverChoice(): boolean {
    return this.nextApproverChoices.length === 1
  }

  get isFinalApproval(): boolean {
    return this.isPending && this.nextApproverChoices.length === 0
  }

  get delegatedApprover() {
    return this.nextApprover?.approvalDelegatee ?? null
  }

  get hasItems(): boolean {
    return this.items.length > 0
  }

  get taxTotal(): number {
    return this.items.reduce(
      (sum, item) => sum + item.taxAmount * item.currency.rate,
      0
    )
  }

  get subtotal(): number {
    return this.items.reduce(
      (sum, item) => sum + item.subtotal * item.currency.rate,
      0
    )
  }

  get categoryLabel(): string {
    return `${this.primaryCategory.name} - ${this.secondaryCategory.name}`
  }

  get hasTravel(): boolean {
    return this.travel !== null
  }

  get hasLogs(): boolean {
    return this.logs.length > 0
  }

  get hasDescription(): boolean {
    return this.description.length > 0
  }

  get deniedBy(): Nullable<Approver> {
    if (this.logs.length === 0) return null
    return (
      [...this.logs].reverse().find((log) => log.isDenyAction)?.user ?? null
    )
  }

  get finalApprovedBy(): Nullable<Approver> {
    if (this.logs.length === 0) return null
    return (
      [...this.logs].reverse().find((log) => log.isFinalApprovedAction)?.user ??
      null
    )
  }
}
